import {
  ArrowClockwiseIcon,
  ArrowSquareOutIcon,
  CheckIcon,
  HeartIcon,
  ListIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
} from '@phosphor-icons/react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';

import {
  ConfidenceDialog,
  ExplanationGroup,
  hasConfidenceFactors,
  MatchSummary,
  ScoreBreakdown,
  SEARCH_RESULTS_PAGE_SIZE,
  useSearchResultDetail,
  useSearchResults,
  type ConfidenceFactors,
  type SearchResultDetailData,
  type SearchResultDetailItem,
} from '@entities/search-run';
import { useSearchSession } from '@entities/search-session';
import { isLive, useSelection, type SelectionSlot, type SelectionState } from '@entities/selection';
import { useVehicle } from '@entities/vehicle/api';
import { AttentionFlags } from '@entities/vehicle/AttentionFlags';
import { formatLocation, formatObservedDate, formatPrice } from '@entities/vehicle/lib';
import { MarketComparison } from '@entities/vehicle/MarketComparison';
import type { VehicleDetailData, VehicleOpenDetailData } from '@entities/vehicle/types';
import { VehicleDetails } from '@entities/vehicle/VehicleDetails';
import { VehicleGallery } from '@entities/vehicle/VehicleGallery';
import { VehicleKeySpecs } from '@entities/vehicle/VehicleKeySpecs';
import { KarlDialogue } from '@features/chat';
import {
  parseResultQuery,
  toVehicleLinkSearch,
  type ResultQuery,
} from '@features/filters/model/resultQuery';
import {
  FavoriteNotice,
  FavoriteToggle,
  ServiceChoice,
  useFavoriteToggle,
  VehicleDealer,
  vehicleLabel,
} from '@features/search';
import { SelectionActions } from '@features/selection';
import { ApiError } from '@shared/api/problem';
import { ROUTES } from '@shared/config/routes';
import { useAppShell, useFocusKarl } from '@shared/lib/appShell';
import { Badge, Button, ButtonLink, Container, KarlIntro, Skeleton, StateBlock } from '@shared/ui';

import styles from './AppVehiclePage.module.css';
import { VehicleNavigation } from './VehicleNavigation';

/**
 * Экран автомобиля в заказе (FE-083; Epic 58, FE-587, макет 04; MVP_PRD §18).
 *
 * Страница собирает два источника и не смешивает их:
 *
 * * **объявление** — `GET /vehicles/{slug}`: фотографии, характеристики,
 *   автохаус, ссылка на объявление; после оплаты ещё рыночное сравнение и
 *   признаки внимания. Это свойства автомобиля, они не зависят от запроса;
 * * **место в заказе** — `GET /search-runs/{run}/results/{vehicleId}`: отметка
 *   избранного и «Auto N von M» в порядке списка; после оплаты ещё Match Score,
 *   причины, компромиссы и разбор уверенности — снимок прогона.
 *
 * **До оплаты оба отвечают уровнем `open`** (BE-580): данные автомобиля своего
 * рабочего набора бесплатны, аналитики в ответах нет физически. Экран тогда —
 * раскладка макета 04: реплика Karl, галерея с сердечком, главные
 * характеристики, блок автохауса, выбор «сам / Karl» и нижняя строка. **После
 * оплаты** — прежний полный экран; к нему добавлены навигация по списку,
 * сердечко и блок автохауса, а выбора «сам / Karl» нет: работа Karl уже
 * оплачена.
 *
 * Запрос приходит из пути (`/app/searches/:searchId/cars/:vehicleId`), прогон и
 * порядок списка — из адреса (`?run=…&sort=…&favorites_only=…&page=…`), иначе
 * берётся текущий прогон **этого** запроса. Без прогона экран заменяется
 * приглашением запустить подбор: выдумывать соответствие «по памяти» нельзя
 * (AC-07).
 *
 * Рельс Karl с диалогом заказа стоит и здесь (CLAUDE.md §8): «Suche anpassen»
 * ведёт в его поле ввода, а не уводит с экрана.
 */
export function AppVehiclePage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const { searchId = '', vehicleId = '' } = useParams();
  const [searchParams] = useSearchParams();

  const sessionQuery = useSearchSession(searchId || null);
  const session = sessionQuery.data ?? null;
  const runId = searchParams.get('run') ?? session?.session.current_search_run_id ?? null;

  // Порядок списка, из которого открыта карточка: по нему backend считает
  // «Auto N von M» (BE-580). Адрес приносит его из витрины вместе с прогоном.
  const listQuery: ResultQuery = { ...parseResultQuery(searchParams), run: runId ?? undefined };
  const detailQuery = useSearchResultDetail(runId, vehicleId || null, {
    sort: listQuery.sort,
    favoritesOnly: listQuery.favoritesOnly,
  });
  const detail = detailQuery.data;
  const open = detail?.access === 'open';

  // Размер рабочего набора и число отметок — для реплик Karl и «Meine
  // Favoriten (n)». Страница в одну карточку без `favorites_only`: с ним
  // `navigation.total` — число отметок, а не набора. Ключ тот же, что у
  // витрины, — запрос обычно уже в кеше.
  const setQuery = useSearchResults(runId, { page: 1, pageSize: 1 }, open);
  const setPage = setQuery.data;

  // Назад — в тот же список: прогон, сортировка, страница и «Meine Favoriten»
  // живут в адресе, и витрина восстанавливается из него целиком.
  const backToList = `${ROUTES.searchResults(searchId)}${toVehicleLinkSearch(listQuery)}`;
  // Соседний автомобиль — с теми же параметрами списка; страница — та, на
  // которой он стоит: «назад» с него вернёт к нему, а не к первой карточке.
  const vehicleHref = (id: string, position: number) =>
    `${ROUTES.searchVehicle(searchId, id)}${toVehicleLinkSearch({
      ...listQuery,
      page: Math.max(1, Math.ceil(position / SEARCH_RESULTS_PAGE_SIZE)),
    })}`;
  const favoritesHref = `${ROUTES.searchResults(searchId)}${toVehicleLinkSearch({
    ...listQuery,
    favoritesOnly: true,
    page: 1,
  })}`;

  // Автомобиль четвёрки: те же два действия, что и на экране Top-4 (§34.4).
  // Пользователь, открывший карточку «Подробнее», не должен возвращаться
  // назад только ради кнопки — решение принимается там, где он смотрит.
  const paid = session?.session.paid ?? false;
  const selectionQuery = useSelection(searchId || null, paid);
  const selection = selectionQuery.data ?? null;
  const slot = selection?.slots.find(
    (item) => isLive(item) && item.vehicle.vehicle_id === vehicleId,
  );

  // Объявление читается по slug: публичный endpoint принимает только его.
  // Slug приходит вместе с местом в заказе, поэтому второй запрос уходит
  // после первого — данных для него до этого просто нет.
  const vehicleQuery = useVehicle(detail?.slug ?? '', locale, detail !== undefined);

  const [confidenceOpen, setConfidenceOpen] = useState(false);

  /** Оболочка: шаг трекера и рельс Karl с диалогом заказа (FE-193, FE-587). */
  useAppShell({
    stage: session?.session.stage ?? null,
    paid,
    karl: { extra: <KarlDialogue searchId={searchId} /> },
  });

  // Подбор не запускался: персонального результата не существует, и
  // «показать что-нибудь похожее» здесь означало бы выдумать соответствие.
  if (runId === null && !sessionQuery.isPending) {
    return (
      <Container>
        <div className={styles.stateWrap}>
          <StateBlock title={t('results.noRun.title')} description={t('results.noRun.text')} />
        </div>
      </Container>
    );
  }

  if (detailQuery.isError) {
    const status = detailQuery.error instanceof ApiError ? detailQuery.error.status : null;
    const backToResults = (
      <ButtonLink to={ROUTES.searchResults(searchId)} variant="secondary">
        {t('results.detail.backToResults')}
      </ButtonLink>
    );

    // `403` значит одно из двух (FE-580): заказ не оплачен — тогда карточка
    // закрыта, а не отсутствует, — либо автомобиль не входит в рабочий набор
    // текущего прогона. Различает их только оплата, поэтому до ответа о
    // заказе состояние не выбирается: мелькнувшее «не оплачено» у оплаченного
    // заказа было бы неправдой.
    if (status === 403 && sessionQuery.isPending) return <PageSkeleton />;

    if (status === 403 && !paid) {
      // Не «оплатите и откроется»: оплата открывает рабочий набор, а не
      // произвольную карточку, и обещать этот автомобиль нельзя (§34.11).
      return (
        <Container>
          <div className={styles.stateWrap}>
            <StateBlock
              title={t('results.detail.locked.title')}
              description={t('results.detail.locked.text')}
              action={backToResults}
            />
          </div>
        </Container>
      );
    }

    if (status === 403 || status === 404) {
      // Автомобиля нет в текущем подборе — так бывает после перестройки, когда
      // ссылка ведёт на автомобиль прежнего прогона. Повтор тут не поможет,
      // поэтому его и нет; есть дорога к результатам и, если автохаусу по
      // этому автомобилю писали, к переписке. История выбора покрывает все
      // прогоны заказа, а не только текущий.
      const negotiationId =
        selection?.history.find(
          (item) => item.vehicle.vehicle_id === vehicleId && item.negotiation_id !== null,
        )?.negotiation_id ?? null;
      return (
        <Container>
          <div className={styles.stateWrap}>
            <StateBlock
              title={t('results.detail.notFound.title')}
              description={t('results.detail.notFound.text')}
              action={
                <div className={styles.stateActions}>
                  {backToResults}
                  {negotiationId === null ? null : (
                    <ButtonLink to={ROUTES.negotiation(negotiationId)} variant="secondary">
                      {t('results.detail.toNegotiation')}
                    </ButtonLink>
                  )}
                </div>
              }
            />
          </div>
        </Container>
      );
    }

    // Сеть, `5xx`, прогон ещё не готов — сбой, и повтор здесь осмыслен.
    return (
      <Container>
        <div className={styles.stateWrap}>
          <StateBlock
            tone="error"
            title={t('results.error.title')}
            description={t('results.error.text')}
            action={
              <div className={styles.stateActions}>
                <Button
                  variant="secondary"
                  iconEnd={<ArrowClockwiseIcon size={16} weight="bold" />}
                  onClick={() => {
                    void detailQuery.refetch();
                  }}
                >
                  {t('error.retry')}
                </Button>
                {backToResults}
              </div>
            }
          />
        </div>
      </Container>
    );
  }

  if (vehicleQuery.isError) {
    return (
      <Container>
        <div className={styles.stateWrap}>
          <StateBlock
            tone="error"
            title={t('vehicle.error.title')}
            description={t('vehicle.error.text')}
            action={
              <ButtonLink to={ROUTES.searchResults(searchId)} variant="secondary">
                {t('results.detail.backToResults')}
              </ButtonLink>
            }
          />
        </div>
      </Container>
    );
  }

  if (detail === undefined || vehicleQuery.data === undefined || runId === null) {
    return <PageSkeleton />;
  }

  // Закрытая карточка своего заказа означает, что автомобиль выпал из набора
  // между двумя запросами: показывать нечего, кроме дороги к списку.
  if (vehicleQuery.data.access === 'teaser') {
    return (
      <Container>
        <div className={styles.stateWrap}>
          <StateBlock
            title={t('results.detail.notFound.title')}
            description={t('results.detail.notFound.text')}
            action={
              <ButtonLink to={backToList} variant="secondary">
                {t('results.detail.backToResults')}
              </ButtonLink>
            }
          />
        </div>
      </Container>
    );
  }

  const vehicle = vehicleQuery.data;
  const navigation = (
    <VehicleNavigation
      backTo={backToList}
      navigation={detail.navigation}
      vehicleHref={vehicleHref}
    />
  );

  if (detail.access === 'open') {
    return (
      <OpenVehicle
        searchId={searchId}
        runId={runId}
        vehicle={vehicle}
        detail={detail}
        locale={locale}
        navigation={navigation}
        setSize={setPage?.total}
        favoritesCount={setPage?.favorites_count}
        backTo={backToList}
        favoritesHref={favoritesHref}
      />
    );
  }

  return (
    <PersonalVehicle
      searchId={searchId}
      runId={runId}
      navigation={navigation}
      vehicle={vehicle}
      detail={detail}
      locale={locale}
      selection={selection}
      slot={slot ?? null}
      confidenceOpen={confidenceOpen}
      onConfidenceOpenChange={setConfidenceOpen}
    />
  );
}

// --- Сердечко на галерее ----------------------------------------------------------------

interface GalleryWithFavoriteProps {
  searchId: string;
  runId: string;
  vehicle: VehicleDetailData | VehicleOpenDetailData;
  detail: SearchResultDetailItem;
}

/**
 * Галерея с сердечком избранного (BE-581, FE-587).
 *
 * Отметка — закладка на заказе: одно нажатие, без подтверждения, сразу во всех
 * списках и на этой карточке. При сбое сердечко возвращается назад, а причина
 * встаёт под галереей — рядом с тем сердечком, которое вернулось.
 */
function GalleryWithFavorite({ searchId, runId, vehicle, detail }: GalleryWithFavoriteProps) {
  const label = vehicleLabel(vehicle);
  const { toggle, failed, pending } = useFavoriteToggle({
    searchId,
    runId,
    vehicleId: detail.vehicle_id,
  });

  return (
    <div className={styles.media}>
      <VehicleGallery
        images={vehicle.images}
        fallbackAlt={`${vehicle.make} ${vehicle.model}`}
        overlay={
          <FavoriteToggle
            pressed={detail.is_favorite}
            onToggle={toggle}
            vehicleLabel={label}
            busy={pending}
          />
        }
      />
      {failed ? <FavoriteNotice failed={failed} name={label} /> : null}
    </div>
  );
}

// --- До оплаты: макет 04 ------------------------------------------------------------------

interface OpenVehicleProps {
  searchId: string;
  runId: string;
  /** Объявление целиком, без аналитики (BE-580). */
  vehicle: VehicleDetailData | VehicleOpenDetailData;
  detail: SearchResultDetailItem;
  locale: string;
  navigation: ReactNode;
  /** Размер рабочего набора; `undefined` — ещё не известен, и чисел в репликах нет. */
  setSize: number | undefined;
  /** Отмеченные автомобили набора; `undefined` — ещё не известны. */
  favoritesCount: number | undefined;
  backTo: string;
  favoritesHref: string;
}

/**
 * Экран автомобиля до оплаты (Epic 58, FE-587, макет 04).
 *
 * Сверху вниз: навигация по списку, реплика Karl, галерея с сердечком и
 * главные характеристики рядом с ценой, блок автохауса, полное описание
 * объявления, вторая реплика Karl, выбор «сам / Karl» и нижняя строка.
 *
 * Чего здесь нет — и это граница бесплатного, а не упрощение: Match Score,
 * причин, рынка, признаков внимания и происхождения данных. Их полей нет в
 * ответе `open`, и нарисовать их нечем.
 *
 * Все числа — из API: размер набора — `total` списка без «Meine Favoriten»,
 * цена услуги — из конфигурации.
 */
function OpenVehicle({
  searchId,
  runId,
  vehicle,
  detail,
  locale,
  navigation,
  setSize,
  favoritesCount,
  backTo,
  favoritesHref,
}: OpenVehicleProps) {
  const { t } = useTranslation();
  const focusKarl = useFocusKarl();
  const observedAt = formatObservedDate(vehicle.data_observed_at, locale);

  return (
    <div className={styles.page}>
      <Container>
        <div className={styles.stack}>
          {navigation}

          {/* Реплика Karl — фиксированный текст из локалей (DESIGN_SYSTEM §5.4),
              число — размер набора из API. Пока его нет — скелетон, а не
              выдуманное число. */}
          {setSize === undefined ? (
            <Skeleton height="4.5rem" radius="var(--radius-card)" />
          ) : (
            <KarlIntro>
              <strong>{t('results.detail.karl.intro', { count: setSize })}</strong>
              <br />
              {t('results.detail.karl.introText', { count: setSize })}
            </KarlIntro>
          )}

          <div className={styles.layout}>
            <GalleryWithFavorite
              searchId={searchId}
              runId={runId}
              vehicle={vehicle}
              detail={detail}
            />

            <div className={styles.summary}>
              <div className={styles.headline}>
                <h1 className={styles.title}>
                  {vehicle.make} {vehicle.model}
                </h1>
                {vehicle.is_new ? (
                  <Badge tone="neutral">
                    {t('catalog.new')}
                    <span className="visually-hidden"> {t('catalog.newHint')}</span>
                  </Badge>
                ) : null}
              </div>
              {vehicle.variant ? <p className={styles.variant}>{vehicle.variant}</p> : null}

              <div className={styles.priceRow}>
                <p className={`${styles.price} tabular`}>{formatPrice(vehicle.price, locale)}</p>
                {/* «Verfügbar» — бейдж макета у опубликованного объявления,
                    решение заказчика 27.10.2026 (TASKS, Epic 58). Это статус
                    объявления на момент обхода источника, а не подтверждение
                    автохауса: дата данных стоит рядом. Тон нейтральный —
                    зелёный в продукте значит «соответствует» (§2.4). */}
                {vehicle.status === 'published' ? (
                  <Badge tone="neutral" icon={<CheckIcon size={14} weight="bold" />}>
                    {t('vehicle.available')}
                  </Badge>
                ) : null}
              </div>
              <p className={styles.priceMeta}>
                {t(`vehicle.vatStatus.${vehicle.vat_status}`)}
                {vehicle.negotiable ? ` · ${t('vehicle.negotiable')}` : ''}
                {observedAt === null ? '' : ` · ${t('vehicle.dataDate')} ${observedAt}`}
              </p>

              <div className={styles.keySpecs}>
                <VehicleKeySpecs specs={vehicle.specs} locale={locale} />
              </div>
            </div>
          </div>

          <VehicleDealer
            runId={runId}
            vehicleId={detail.vehicle_id}
            seller={vehicle.seller}
            listingUrl={vehicle.listing_url}
            showFreeNote
          />

          <VehicleDetails vehicle={vehicle} locale={locale} />

          <KarlIntro>{t('results.detail.karl.choice')}</KarlIntro>

          <ServiceChoice
            searchId={searchId}
            runId={runId}
            vehicleId={detail.vehicle_id}
            listingUrl={vehicle.listing_url}
            setSize={setSize}
          />

          {/* Нижняя строка (макет 04): к списку, уточнить запрос у Karl,
              «Meine Favoriten». Все три — синий контур: главное действие на
              экране одно, и оно в колонке «Karl übernimmt». */}
          <div className={styles.actions}>
            <ButtonLink to={backTo} variant="outline-action">
              <span className={styles.actionLabel}>
                <ListIcon size={16} aria-hidden="true" />
                {t('results.detail.actions.toList')}
              </span>
            </ButtonLink>
            {focusKarl ? (
              <Button
                variant="outline-action"
                onClick={() => {
                  focusKarl(t('showcase.actions.adjustPrefill'));
                }}
              >
                <span className={styles.actionLabel}>
                  <MagnifyingGlassIcon size={16} aria-hidden="true" />
                  {t('showcase.actions.adjust')}
                </span>
              </Button>
            ) : null}
            {favoritesCount === undefined ? null : (
              <ButtonLink to={favoritesHref} variant="outline-action">
                <span className={styles.actionLabel}>
                  <HeartIcon size={16} aria-hidden="true" />
                  <span className="tabular">
                    {t('showcase.actions.favorites', { count: favoritesCount })}
                  </span>
                </span>
              </ButtonLink>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
}

// --- После оплаты: прежний полный экран ----------------------------------------------------

interface PersonalVehicleProps {
  searchId: string;
  runId: string;
  navigation: ReactNode;
  vehicle: VehicleDetailData | VehicleOpenDetailData;
  detail: SearchResultDetailData;
  locale: string;
  /** Состав четвёрки заказа. `null` — заказ не оплачен либо четвёрки ещё нет. */
  selection: SelectionState | null;
  /** Слот этого автомобиля в четвёрке. `null` — автомобиль в неё не входит. */
  slot: SelectionSlot | null;
  confidenceOpen: boolean;
  onConfidenceOpenChange: (open: boolean) => void;
}

/**
 * Полный экран оплаченного заказа (FE-083): объявление, разбор соответствия,
 * рыночное сравнение и признаки внимания. С Epic 58 к нему добавлены навигация
 * по списку, сердечко на галерее и блок автохауса (FE-587); выбора «сам /
 * Karl» нет — работа Karl уже оплачена.
 */
function PersonalVehicle({
  searchId,
  runId,
  navigation,
  vehicle,
  detail,
  locale,
  selection,
  slot,
  confidenceOpen,
  onConfidenceOpenChange,
}: PersonalVehicleProps) {
  const { t } = useTranslation();
  const noData = t('catalog.noData');
  const observedAt = formatObservedDate(vehicle.data_observed_at, locale);
  const currency = vehicle.price.currency;
  // Аналитика объявления приходит только на уровне `full`: у `open` её полей
  // нет в ответе, и блоки не рисуются, а не рисуются пустыми.
  const analytics = vehicle.access === 'full' ? vehicle : null;
  const factors: ConfidenceFactors | Record<string, never> = detail.confidence_factors;

  return (
    <div className={styles.page}>
      <Container>
        <div className={styles.navWrap}>{navigation}</div>

        {detail.is_stale ? (
          <p className={styles.stale} role="status">
            {t('results.detail.stale')}
          </p>
        ) : null}

        <div className={styles.layout}>
          <GalleryWithFavorite
            searchId={searchId}
            runId={runId}
            vehicle={vehicle}
            detail={detail}
          />

          <div className={styles.summary}>
            <div className={styles.headline}>
              <h1 className={styles.title}>
                {vehicle.make} {vehicle.model}
              </h1>
              {vehicle.is_new ? (
                <Badge tone="neutral">
                  {t('catalog.new')}
                  <span className="visually-hidden"> {t('catalog.newHint')}</span>
                </Badge>
              ) : null}
              <Badge tone="info">{t('results.detail.rank', { rank: detail.rank })}</Badge>
            </div>
            <p className={styles.variant}>{vehicle.variant ?? noData}</p>

            <p className={`${styles.price} tabular`}>{formatPrice(vehicle.price, locale)}</p>

            {/* Блок появляется только у автомобиля, который сейчас занимает
                слот четвёрки: кнопка выбора у прочих результатов означала бы
                выбор вне четвёрки, а его backend не примет (§34.4). */}
            {selection !== null && slot !== null ? (
              <div className={styles.selectionActions}>
                <SelectionActions
                  searchId={searchId}
                  slot={slot}
                  selection={selection}
                  layout="page"
                />
              </div>
            ) : null}
            <p className={styles.priceMeta}>
              {t(`vehicle.vatStatus.${vehicle.vat_status}`)}
              {vehicle.negotiable ? ` · ${t('vehicle.negotiable')}` : ''}
            </p>

            <dl className={styles.facts}>
              <div>
                <dt>
                  <MapPinIcon size={16} aria-hidden="true" />
                  {t('catalog.spec.location')}
                </dt>
                <dd>{formatLocation(vehicle.location, noData)}</dd>
              </div>
              <div>
                <dt>{t('vehicle.dataDate')}</dt>
                <dd>{observedAt ?? noData}</dd>
              </div>
            </dl>

            {analytics?.source?.url ? (
              <a
                className={styles.sourceLink}
                href={analytics.source.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                {t('vehicle.sourceLink', { name: analytics.source.name })}
                <ArrowSquareOutIcon size={16} aria-hidden="true" />
              </a>
            ) : null}
          </div>
        </div>

        <div className={styles.dealerWrap}>
          <VehicleDealer
            runId={runId}
            vehicleId={detail.vehicle_id}
            seller={vehicle.seller}
            listingUrl={vehicle.listing_url}
            showFreeNote={false}
          />
        </div>

        {/* Персональный блок: почему автомобиль подходит именно этому запросу. */}
        <div className={styles.personal}>
          <MatchSummary
            match={detail.match}
            inapplicable={detail.inapplicable_components}
            onOpenConfidence={
              hasConfidenceFactors(factors)
                ? () => {
                    onConfidenceOpenChange(true);
                  }
                : undefined
            }
          />

          <div className={styles.explanations}>
            <ExplanationGroup kind="why" items={detail.why_matches} currency={currency} />
            <ExplanationGroup kind="advantages" items={detail.advantages} currency={currency} />
            <ExplanationGroup kind="neutral" items={detail.neutral_facts} currency={currency} />
            <ExplanationGroup kind="compromises" items={detail.compromises} currency={currency} />
          </div>

          <ScoreBreakdown components={detail.components} />
        </div>

        {/*
          Рыночное сравнение и признаки внимания — свойства объявления, а не
          подбора: они показываются из текущих данных автомобиля. `null`
          означает «аналитика не рассчитывалась» — пустой блок читался бы как
          «посчитали и ничего не нашли».
        */}
        {analytics !== null && (analytics.market !== null || analytics.attention_flags !== null) ? (
          <div className={styles.analytics}>
            {analytics.market !== null ? (
              <MarketComparison
                vehicleId={analytics.id}
                price={analytics.price}
                market={analytics.market}
                source={analytics.source}
              />
            ) : null}
            {analytics.attention_flags !== null ? (
              <AttentionFlags
                vehicleId={analytics.id}
                currency={currency}
                flags={analytics.attention_flags}
                source={analytics.source}
              />
            ) : null}
          </div>
        ) : null}

        <div className={styles.detailsWrap}>
          <VehicleDetails vehicle={vehicle} locale={locale} />
        </div>
      </Container>

      {hasConfidenceFactors(factors) ? (
        <ConfidenceDialog
          open={confidenceOpen}
          onClose={() => {
            onConfidenceOpenChange(false);
          }}
          factors={factors}
          currency={currency}
        />
      ) : null}
    </div>
  );
}

function PageSkeleton() {
  const { t } = useTranslation();
  return (
    <div className={styles.page}>
      <Container>
        <p className="visually-hidden" role="status">
          {t('vehicle.loading')}
        </p>
        <div className={styles.layout} aria-hidden="true">
          <div className={styles.media}>
            <div className={styles.skeletonStage}>
              <Skeleton height="100%" radius="var(--radius-card)" />
            </div>
          </div>
          <div className={styles.summary}>
            <Skeleton height="2rem" width="70%" />
            <Skeleton height="1rem" width="45%" />
            <Skeleton height="2.5rem" width="55%" />
            <Skeleton height="1rem" width="80%" />
          </div>
        </div>
        <div className={styles.personal} aria-hidden="true">
          <Skeleton height="12rem" radius="var(--radius-panel)" />
        </div>
      </Container>
    </div>
  );
}
